import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import {
  Compass,
  Mountain,
  HeartPulse,
  Wind,
  Users,
  Network,
  Sparkles,
  Flame,
} from 'lucide-react';

const themes = [
  {
    n: '01',
    icon: Mountain,
    title: 'Presence under pressure',
    body: 'Staying yourself when the room, the quarter, or the moment is asking you to disappear into it.',
  },
  {
    n: '02',
    icon: Compass,
    title: 'The shadow side of your strengths',
    body: 'The very capacities that built your career are often the ones quietly costing you now. Naming them changes them.',
  },
  {
    n: '03',
    icon: HeartPulse,
    title: 'What your body already knows',
    body: 'The signal that arrives before the thought. Learning to lead from a steadier nervous system as well as a sharper mind.',
  },
  {
    n: '04',
    icon: Wind,
    title: 'The field you walk into',
    body: 'The unspoken culture you create the moment you enter a room. What it invites. What it shuts down. What it costs people to be near you.',
  },
  {
    n: '05',
    icon: Users,
    title: 'The relationships that move the work',
    body: 'The handful of people whose trust, candor, or quiet resistance is shaping outcomes more than any strategy deck.',
  },
  {
    n: '06',
    icon: Network,
    title: "The system you're inside of",
    body: "The structures, incentives, and inherited rules that keep producing the results you're trying to change. Seeing them is half the work.",
  },
  {
    n: '07',
    icon: Flame,
    title: 'Power without performance',
    body: "Leading from a place that doesn't require you to be on. What it costs to keep performing. What becomes possible when you stop.",
  },
  {
    n: '08',
    icon: Sparkles,
    title: 'The next identity',
    body: "The version of you that's asking to emerge, and the version that has to be allowed to end so it can.",
  },
];

const ThemeCard = ({
  item,
  index,
}: {
  item: typeof themes[number];
  index: number;
}) => {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const Icon = item.icon;
  return (
    <article
      ref={ref}
      style={{ transitionDelay: visible ? `${(index % 4) * 80}ms` : '0ms' }}
      className={`group relative overflow-hidden rounded-sm p-8 sm:p-10 transition-all duration-700
        bg-card
        border border-border
        hover:border-primary/60 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {/* Decorative terracotta corner accent */}
      <div
        className="absolute top-0 right-0 w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at top right, hsl(var(--primary) / 0.18) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      {/* (Number watermark removed) */}

      {/* Icon + index row */}
      <div className="relative flex items-center gap-4 mb-7">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors duration-500">
          <Icon
            className="w-5 h-5 text-primary group-hover:scale-110 transition-transform duration-500"
            aria-hidden="true"
          />
        </div>
        <span className="h-px flex-1 bg-border group-hover:bg-primary/40 transition-colors duration-500" />
      </div>

      <h3 className="relative text-foreground font-display text-2xl sm:text-[1.65rem] mb-4 leading-tight group-hover:text-primary transition-colors duration-500">
        {item.title}
      </h3>
      <p className="relative text-foreground leading-relaxed text-base sm:text-lg font-light">
        {item.body}
      </p>
    </article>
  );
};

const InnerTerritory = () => {
  const heading = useScrollReveal<HTMLDivElement>();

  return (
    <section className="relative bg-foreground text-background py-32 sm:py-44 overflow-hidden">
      {/* Layered painterly washes for depth */}
      <div
        className="absolute inset-0 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(ellipse at 15% 10%, hsl(var(--primary) / 0.18) 0%, transparent 50%), radial-gradient(ellipse at 85% 90%, hsl(var(--sage) / 0.10) 0%, transparent 55%)',
        }}
        aria-hidden="true"
      />
      {/* Subtle grid lines for editorial structure */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--background)) 1px, transparent 1px)',
          backgroundSize: '120px 100%',
        }}
        aria-hidden="true"
      />

      <div className="container-mobile relative z-10 max-w-7xl mx-auto">
        {/* Header — left-aligned editorial */}
        <div
          ref={heading.ref}
          className={`max-w-3xl mb-20 transition-all duration-1000 ${
            heading.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-12 bg-primary" aria-hidden="true" />
            <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
              What We Work On
            </p>
          </div>
          <h2
            className="text-background text-4xl sm:text-5xl lg:text-6xl mb-6 leading-[1.05]"
            style={{ textWrap: 'balance' }}
          >
            The whole <span className="text-primary italic">territory.</span>
          </h2>
          <p className="text-background/70 text-xl leading-relaxed font-light max-w-xl">
            Increasing awareness to expand our choice of response.
          </p>
        </div>

        {/* Card grid */}
        <div className="grid gap-6 md:gap-8 md:grid-cols-2 lg:grid-cols-2">
          {themes.map((item, i) => (
            <ThemeCard key={item.n} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default InnerTerritory;
